import { NudgeCardV2 } from "@/components/interactive/NudgeCardV2";
import { AmplitudeEvent, useAmplitudeTrack } from "@/utils/amplitude";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import React, { useEffect, useRef } from "react";

export interface DismissableNudgeAction {
  buttonText: string;
  buttonOnClick: () => void;
}

export interface DismissableNudgeProps {
  id: string;
  title: string;
  description: string;
  actions: DismissableNudgeAction[];
  storageKey: LocalStorageKey;
  /** Controls eligibility independently from persisted dismissal state. */
  shouldShow?: boolean;
  /** Persists dismissal when the caller determines the user has found the feature. */
  dismissWhen?: boolean;
  /** Called after an explicit close-button dismissal. */
  onDismiss?: () => void;
  placement?: "bottom-left" | "bottom-right";
  bottomOffset?: number;
}

interface DismissableNudgeState extends Record<string, unknown> {
  dismissedAt: string | null;
}

const UNDISMISSED: DismissableNudgeState = { dismissedAt: null };

/** Clears a nudge's stored dismissal state. */
export const useResetDismissableNudge = (storageKey: LocalStorageKey) => {
  const [, setState] = useLocalStorageSyncedState(storageKey, UNDISMISSED);
  return () => setState(null);
};

type NudgeTrackingProperties = {
  id: string;
  title: string;
  time_spent_hovering_ms: number;
};

/** A reusable nudge with persisted dismissal state and consistent interaction analytics. */
export const DismissableNudge: React.FC<DismissableNudgeProps> = ({
  id,
  title,
  description,
  actions,
  storageKey,
  shouldShow = true,
  dismissWhen = false,
  onDismiss,
  placement,
  bottomOffset,
}) => {
  const [nudge, setNudge] = useLocalStorageSyncedState(storageKey, UNDISMISSED);
  const dismissed = nudge.dismissedAt != null;
  const track = useAmplitudeTrack();
  const hoverStartedAt = useRef<number | null>(null);
  const totalHoverDurationMs = useRef(0);

  const getTrackingProperties = (): NudgeTrackingProperties => ({
    id,
    title,
    time_spent_hovering_ms:
      totalHoverDurationMs.current +
      (hoverStartedAt.current == null ? 0 : Math.max(0, Date.now() - hoverStartedAt.current)),
  });

  const dismiss = () => setNudge({ dismissedAt: new Date().toISOString() });

  useEffect(() => {
    if (dismissWhen && !dismissed) {
      setNudge({ dismissedAt: new Date().toISOString() });
    }
  }, [dismissWhen, dismissed, setNudge]);

  const startHover = () => {
    if (hoverStartedAt.current == null) {
      hoverStartedAt.current = Date.now();
    }
  };

  const finishHover = () => {
    if (hoverStartedAt.current != null) {
      totalHoverDurationMs.current += Math.max(0, Date.now() - hoverStartedAt.current);
      hoverStartedAt.current = null;
    }
    track(AmplitudeEvent.NUDGE_CARD_HOVER_MOUSE, getTrackingProperties());
  };

  const handleDismiss = () => {
    track(AmplitudeEvent.NUDGE_CARD_DISMISS, getTrackingProperties());
    dismiss();
    onDismiss?.();
  };

  if (!shouldShow || dismissed) return null;

  return (
    <NudgeCardV2
      title={title}
      description={description}
      actions={actions.map(({ buttonText, buttonOnClick }) => ({
        label: buttonText,
        onClick: () => {
          track(AmplitudeEvent.NUDGE_CARD_CLICK_ACTION, getTrackingProperties());
          dismiss();
          buttonOnClick();
        },
      }))}
      onDismiss={handleDismiss}
      onMouseEnter={startHover}
      onMouseLeave={finishHover}
      placement={placement}
      bottomOffset={bottomOffset}
    />
  );
};
